import { useCallback } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import {
  GenerateUrlRequestBody,
  Paginated,
  PaginationQuery,
  UpdateOrderRequestBody,
  UpdateVisibilityRequestBody,
} from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import {
  AdminWallMaterialFixing,
  InsertWallMaterialFixingBody,
  UpdateWallMaterialFixingNewImageBody,
  UpdateWallMaterialFixingRemoveImageBody,
} from "./types/wallMaterialFixings";

export const useWallMaterialFixingsApi = () => {
  const api = useApi();

  const getAdminWallMaterialFixing = useCallback(
    (signal: AbortSignal) => {
      const url = `/admin/settings/vertigrip/wall_material_fixings`;
      return useSWRMutation<AdminWallMaterialFixing, CustomError, string, string>(
        url,
        (url, { arg }: { arg: string }) => api.get<AdminWallMaterialFixing>({ url: `${url}/${arg}`, signal }),
      );
    },
    [api],
  );

  const useAdminWallMaterialFixings = useCallback(
    (wallMaterialId: string, query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/vertigrip/wall_materials/${wallMaterialId}/fixings`, query);
      return useSWR<Paginated<AdminWallMaterialFixing>, CustomError, string>(url, (url) =>
        api.get<Paginated<AdminWallMaterialFixing>>({ url, signal }),
      );
    },
    [api],
  );

  const insertAdminWallMaterialFixing = useCallback(
    async (body: InsertWallMaterialFixingBody): Promise<{ id: string }> => {
      const url = `/admin/settings/vertigrip/wall_material_fixings`;
      const id = await api.post<{ id: string }, InsertWallMaterialFixingBody>(url, body);
      mutateCache([url, `/admin/settings/vertigrip/wall_materials/${body.wallMaterialId}/fixings`]);
      return id;
    },
    [api],
  );

  const updateAdminWallMaterialFixing = useCallback(
    async (
      wallMaterialFixingId: string,
      body: UpdateWallMaterialFixingRemoveImageBody | UpdateWallMaterialFixingNewImageBody,
    ): Promise<void> => {
      const url = `/admin/settings/vertigrip/wall_material_fixings/${wallMaterialFixingId}`;
      (await api.put<UpdateWallMaterialFixingRemoveImageBody | UpdateWallMaterialFixingNewImageBody>(url, body),
        mutateCache([
          `/admin/settings/vertigrip/wall_material_fixings`,
          `/admin/settings/vertigrip/wall_material_fixings/${wallMaterialFixingId}/fixings`,
          `/admin/settings/vertigrip/wall_materials/${body.wallMaterialId}/fixings`,
        ]));
    },
    [api],
  );

  const updateAdminWallMaterialFixingVisibility = useCallback(
    async (
      wallMaterialFixingId: string,
      visibility: UpdateVisibilityRequestBody,
      options?: { wallMaterialId?: string },
    ): Promise<void> => {
      const url = `/admin/settings/vertigrip/wall_material_fixings/${wallMaterialFixingId}/visibility`;
      await api.put<UpdateVisibilityRequestBody>(url, visibility);
      mutateCache([
        `/admin/settings/vertigrip/wall_material_fixings`,
        `/admin/settings/vertigrip/wall_material_fixings/${wallMaterialFixingId}/fixings`,
        `/admin/settings/vertigrip/wall_materials/${options?.wallMaterialId}/fixings`,
      ]);
    },
    [api],
  );

  const updateAdminWallMaterialFixingOrder = useCallback(
    async (
      wallMaterialFixingId: string,
      order: UpdateOrderRequestBody,
      options?: { wallMaterialId?: string },
    ): Promise<void> => {
      const url = `/admin/settings/vertigrip/wall_material_fixings/${wallMaterialFixingId}/order`;
      await api.put<UpdateOrderRequestBody>(url, order);
      mutateCache([
        `/admin/settings/vertigrip/wall_material_fixings`,
        `/admin/settings/vertigrip/wall_material_fixings/${wallMaterialFixingId}/fixings`,
        `/admin/settings/vertigrip/wall_materials/${options?.wallMaterialId}/fixings`,
      ]);
    },
    [api],
  );

  const deleteAdminWallMaterialFixing = useCallback(
    async (wallMaterialFixingId: string, wallMaterialId: string): Promise<void> => {
      const url = `/admin/settings/vertigrip/wall_material_fixings/${wallMaterialFixingId}`;
      await api.del(url);
      mutateCache([
        `/admin/settings/vertigrip/wall_material_fixings`,
        `/admin/settings/vertigrip/wall_material_fixings/${wallMaterialFixingId}`,
        `/admin/settings/vertigrip/wall_materials/${wallMaterialId}/fixings`,
        `/admin/settings/vertigrip/wall_materials/${wallMaterialId}/fixings/${wallMaterialFixingId}`,
      ]);
    },
    [api],
  );

  const generateUploadImageUrl = useCallback(
    async (wallMaterialFixingId: string, fileName: string, fileType: string): Promise<string> => {
      const encodedFileName = encodeURIComponent(fileName);
      const url = `/admin/settings/vertigrip/wall_material_fixings/${wallMaterialFixingId}/image/generate_upload_url?file_name=${encodedFileName}`;
      return api.post<string, GenerateUrlRequestBody>(url, {
        filename: fileName,
        contentType: fileType,
      });
    },
    [api],
  );

  return {
    getAdminWallMaterialFixing,
    useAdminWallMaterialFixings,
    insertAdminWallMaterialFixing,
    updateAdminWallMaterialFixing,
    updateAdminWallMaterialFixingVisibility,
    updateAdminWallMaterialFixingOrder,
    deleteAdminWallMaterialFixing,
    generateUploadImageUrl,
  };
};
